// lib/api/location.ts
import axiosInstance from "./axios";
import { API_ENDPOINTS } from "./config";
import { ApiError } from "./error";

// 타입 정의
export interface LocationType {
  value: string;
  label: string;
}

export interface OperationStatus {
  value: string;
  label: string;
}

export interface DeviceStatus {
  value: string;
  label: string;
}

export const LOCATION_TYPES: LocationType[] = [
  { value: "양돈", label: "양돈" },
  { value: "양계", label: "양계" },
  { value: "한우", label: "한우" },
  { value: "젖소", label: "젖소" },
  { value: "기타", label: "기타" },
];

export const OPERATION_STATUS: OperationStatus[] = [
  { value: "ACTIVE", label: "운영중" },
  { value: "INACTIVE", label: "휴업" },
  { value: "CLOSED", label: "폐업" },
];

export const DEVICE_STATUS: DeviceStatus[] = [
  { value: "ACTIVE", label: "활성" },
  { value: "INACTIVE", label: "비활성" },
  { value: "MAINTENANCE", label: "점검중" },
  { value: "REMOVED", label: "제거됨" },
];

// 소재지 생성 DTO
export interface LocationCreateDto {
  locationCode: string;
  locationName: string;
  locationType: string;
  businessNumber?: string;
  representative?: string;
  contact?: string;
  email?: string;
  regionCode?: string;
  province: string;
  city: string;
  district?: string;
  detailAddress?: string;
  latitude: string;
  longitude: string;
  establishmentDate?: string;
  businessScale?: string;
  employeeCount?: number;
  maxDeviceCount?: number;
  notes?: string;
}

// 소재지 응답 DTO
export interface LocationResponseDto {
  id: number;
  locationCode: string;
  locationName: string;
  locationType: string;
  representative?: string;
  contact?: string;
  fullAddress: string;
  operationStatus: string;
  maxDeviceCount: number;
  currentDeviceCount: number;
  activeDeviceCount: number;
  availableSlots: number;
  createdAt: string;
  updatedAt?: string;
  // 추가 지역 정보 필드
  district?: string;
  city?: string;
  province?: string;
}

// 소재지 상세 응답 DTO
export interface LocationDetailResponseDto extends LocationResponseDto {
  businessNumber?: string;
  email?: string;
  regionCode?: string;
  province: string;
  city: string;
  district?: string;
  detailAddress?: string;
  latitude: number;
  longitude: number;
  establishmentDate?: string;
  businessScale?: string;
  employeeCount?: number;
  notes?: string;
  devices?: LocationDeviceDto[];
  photos?: LocationPhotoDto[];
  odorInfo?: string;
  isPublic?: boolean;
}

// 소재지-기기 연결 DTO
// location.ts의 타입 정의 수정
export interface LocationDeviceDto {
  id: number;
  deviceId: number;
  deviceCode: string;
  deviceName?: string;
  deviceModel?: string;
  sensorNumber: number;
  sensorAlias?: string;
  installationDate: string;
  installationPurpose?: string;
  installationPosition?: string;
  isPrimary: boolean;
  status: string;
  notes?: string;
  // device 필드 - DeviceResponseDto 타입
  device?: {
    id: number;
    deviceId: string;
    brand?: string;
    status?: string;
    regionFullName?: string;
    installation?: {
      deviceName?: string;
      latitude?: number;
      longitude?: number;
      locationDetail?: string;
    };
    photos?: Array<{
      id: number;
      photoType: string;
      fileName?: string;
      filePath?: string;
      fileUrl?: string;
      fileSize?: number;
    }>;
  };
}

// 소재지 사진 DTO
export interface LocationPhotoDto {
  id: number;
  photoType: string;
  fileName: string;
  filePath: string;
  fileUrl?: string;
  fileSize?: number;
  description?: string;
  uploadedAt: string;
}

// 기기 추가 DTO
export interface AddDeviceToLocationDto {
  deviceId: number;
  sensorNumber?: number;
  sensorAlias?: string;
  installationDate: string;
  installationPurpose?: string;
  installationPosition?: string;
  isPrimary?: boolean;
  status?: string;
  notes?: string;
}

// API 엔드포인트 추가
const LOCATION_ENDPOINTS = {
  LIST: "/api/locations",
  DETAIL: (id: number) => `/api/locations/${id}`,
  CREATE: "/api/locations",
  UPDATE: (id: number) => `/api/locations/${id}`,
  DELETE: (id: number) => `/api/locations/${id}`,
  CHECK_CODE: "/api/locations/check-code",
  SEARCH: "/api/locations/search",
  UPDATE_STATUS: (id: number) => `/api/locations/${id}/status`,
  ADD_DEVICE: (id: number) => `/api/locations/${id}/devices`,
  DEVICES: (id: number) => `/api/locations/${id}/devices`,
  REMOVE_DEVICE: (locationId: number, deviceId: number) =>
    `/api/locations/${locationId}/devices/${deviceId}`,
  UPDATE_DEVICE_STATUS: (locationId: number, deviceId: number) =>
    `/api/locations/${locationId}/devices/${deviceId}/status`,
  AVAILABLE: "/api/locations/available",
  STATS: "/api/locations/stats",
  UPLOAD_PHOTO: "/api/locations/photos",
  DELETE_PHOTO: (id: number) => `/api/locations/photos/${id}`,
};

// API 함수들

// 소재지 등록
export const createLocationApi = async (
  data: LocationCreateDto
): Promise<LocationDetailResponseDto> => {
  try {
    const response = await axiosInstance.post<LocationDetailResponseDto>(
      LOCATION_ENDPOINTS.CREATE,
      data
    );
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 목록 조회
export const getLocationsApi = async (params?: {
  page?: number;
  size?: number;
  sort?: string;
  locationType?: string;
  operationStatus?: string;
  regionCode?: string;
}): Promise<{
  content: LocationResponseDto[];
  totalElements: number;
  totalPages: number;
  number: number;
}> => {
  try {
    const response = await axiosInstance.get(LOCATION_ENDPOINTS.LIST, { params });
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 검색
export const searchLocationsApi = async (
  keyword: string,
  params?: { page?: number; size?: number }
): Promise<{
  content: LocationResponseDto[];
  totalElements: number;
  totalPages: number;
  number: number;
}> => {
  try {
    const response = await axiosInstance.get(LOCATION_ENDPOINTS.SEARCH, {
      params: { keyword, ...params },
    });
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 상세 조회
export const getLocationDetailApi = async (id: number): Promise<LocationDetailResponseDto> => {
  try {
    const response = await axiosInstance.get<LocationDetailResponseDto>(
      LOCATION_ENDPOINTS.DETAIL(id)
    );
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 수정
export const updateLocationApi = async (
  id: number,
  data: LocationCreateDto
): Promise<LocationDetailResponseDto> => {
  try {
    const response = await axiosInstance.put<LocationDetailResponseDto>(
      LOCATION_ENDPOINTS.UPDATE(id),
      data
    );
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 삭제
export const deleteLocationApi = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(LOCATION_ENDPOINTS.DELETE(id));
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 코드 중복 체크
export const checkLocationCodeApi = async (
  locationCode: string
): Promise<{
  duplicate: boolean;
  locationCode: string;
}> => {
  try {
    const response = await axiosInstance.get(LOCATION_ENDPOINTS.CHECK_CODE, {
      params: { locationCode },
    });
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 운영상태 변경
export const updateLocationStatusApi = async (id: number, status: string): Promise<void> => {
  try {
    await axiosInstance.patch(LOCATION_ENDPOINTS.UPDATE_STATUS(id), { status });
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지에 기기 추가
export const addDeviceToLocationApi = async (
  locationId: number,
  data: AddDeviceToLocationDto
): Promise<LocationDeviceDto> => {
  try {
    const response = await axiosInstance.post<LocationDeviceDto>(
      LOCATION_ENDPOINTS.ADD_DEVICE(locationId),
      data
    );
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지의 기기 목록 조회
export const getLocationDevicesApi = async (locationId: number): Promise<LocationDeviceDto[]> => {
  try {
    const response = await axiosInstance.get<LocationDeviceDto[]>(
      LOCATION_ENDPOINTS.DEVICES(locationId)
    );
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지에서 기기 제거
export const removeDeviceFromLocationApi = async (
  locationId: number,
  deviceId: number
): Promise<void> => {
  try {
    await axiosInstance.delete(LOCATION_ENDPOINTS.REMOVE_DEVICE(locationId, deviceId));
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지의 기기 상태 변경
export const updateDeviceStatusApi = async (
  locationId: number,
  deviceId: number,
  status: string
): Promise<void> => {
  try {
    await axiosInstance.patch(LOCATION_ENDPOINTS.UPDATE_DEVICE_STATUS(locationId, deviceId), {
      status,
    });
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 기기 할당 가능한 소재지 목록
export const getAvailableLocationsApi = async (): Promise<LocationResponseDto[]> => {
  try {
    const response = await axiosInstance.get<LocationResponseDto[]>(LOCATION_ENDPOINTS.AVAILABLE);
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 소재지 통계 조회
export const getLocationStatsApi = async (): Promise<{
  totalLocations: number;
  locationsByType: Record<string, number>;
  locationsByStatus: Record<string, number>;
  locationsByDistrict: Array<[string, number]>;
  devicesByStatus: Record<string, number>;
}> => {
  try {
    const response = await axiosInstance.get(LOCATION_ENDPOINTS.STATS);
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 사진 업로드
export const uploadLocationPhotoApi = async (
  formData: FormData
): Promise<{
  success: boolean;
  filePath: string;
  fileUrl: string;
  fileName: string;
}> => {
  try {
    const response = await axiosInstance.post(LOCATION_ENDPOINTS.UPLOAD_PHOTO, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 사진 삭제
export const deleteLocationPhotoApi = async (photoId: number): Promise<void> => {
  try {
    await axiosInstance.delete(LOCATION_ENDPOINTS.DELETE_PHOTO(photoId));
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
};

// 이미지 URL 생성 헬퍼 함수
export const getLocationImageUrl = (filePath: string): string => {
  if (!filePath) return "";

  // 이미 완전한 URL인 경우
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }

  // API_BASE_URL은 환경변수나 설정에서 가져옴
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";

  // filePath가 /로 시작하면 제거
  const cleanPath = filePath.startsWith("/") ? filePath.substring(1) : filePath;

  return `${baseUrl}/api/files/location-photos/${cleanPath.split("/").pop()}`;
};
